import { create } from "zustand"
import { persist } from "zustand/middleware"

import type {
	SidebarSection,
	StoredSidebarLayout,
} from "@productlane/lib/src/sidebar-layout"

interface CustomizeSidebarState {
	isOpen: boolean
	setOpen: (open: boolean) => void
}

// Lives outside the sidebar because the hover panel that opens it unmounts once the pointer leaves for the dialog.
export const useCustomizeSidebarState = create<CustomizeSidebarState>()(
	(set) => ({
		isOpen: false,
		setOpen: (isOpen) => {
			set({ isOpen })
		},
	}),
)

interface SidebarSectionFoldState {
	folded: Partial<Record<SidebarSection, boolean>>
	toggle: (section: SidebarSection) => void
}

export const useSidebarSectionFoldState = create<SidebarSectionFoldState>()(
	persist(
		(set) => ({
			folded: {},
			toggle: (section) => {
				set((state) => ({
					folded: { ...state.folded, [section]: !state.folded[section] },
				}))
			},
		}),
		{ name: "sidebar-section-fold-state" },
	),
)

interface SidebarLayoutCacheState {
	byMember: Record<string, StoredSidebarLayout | null>
	remember: (member: string, layout: StoredSidebarLayout | null) => void
}

// The last layout Zero returned per member, so a reload draws the customized nav
// on the first frame instead of the default one until the sync catches up.
export const useSidebarLayoutCache = create<SidebarLayoutCacheState>()(
	persist(
		(set) => ({
			byMember: {},
			remember: (member, layout) => {
				set((state) => ({ byMember: { ...state.byMember, [member]: layout } }))
			},
		}),
		{ name: "sidebar-layout-cache" },
	),
)
